/**
 * The background universe's look, shared by the projection (apps/visuals) and the phones
 * (apps/join): its default shader values and where its dust sits. Only the particle count
 * differs between the two, so the phone shows a sparser copy of the same field.
 */

/** World radii of the dust volume around the sparks; the shell sits at ~3.2. */
export const UNIVERSE_INNER = 4.5;
export const UNIVERSE_OUTER = 32;

/**
 * The camera never turns round, so only the half facing it is filled: direction z up to
 * this, a little past the equator so a sideways fly-to still has dust at the edges.
 */
const HEMISPHERE_Z = 0.15;

/** Nebula clumps, and the share of dust that gathers in them rather than the open field. */
const CLUSTER_COUNT = 10;
const CLUMP_SHARE = 0.3;

/** Share of the open field that are bright stars which flash with a small cross. */
const GLINT_SHARE = 0.025;

/** Share of the open field tinted towards `palette.universe.warm`. */
const WARM_STAR_SHARE = 0.7;

/** Default values of the universe shaders' tunable uniforms. */
export const universeDefaults = {
  /** How fast the field turns around the sparks. */
  speed: 0.05,
  /** How fast the dust drifts. */
  flow: 0.06,
  size: 0.045,
  haze: 0.8,
  twinkle: 0.3,
  glint: 0.6,
  /** World distance from the camera; past the sparks so near dust turns to bokeh. */
  focus: 18,
  aperture: 0.25,
  opacity: 0.35,
  /** Clear radius around the sparks. */
  inner: 5,
  /** Tilt of the spin axis off the view axis, so the turn reads as depth. */
  tiltX: 0.12,
  tiltY: 0.08,
} as const;

/** The projection's resting camera (apps/visuals/src/render/renderer.ts): distance and vertical fov. */
export const HOME_CAMERA_Z = 9;
export const HOME_FOV = 50;

/** Radius of the far sphere the backdrop's sheens are painted on; just past the dust. */
export const BACKDROP_RADIUS = 40;

/**
 * Half-angles (radians, seen from the centre) of the backdrop sphere that the home camera
 * spans at this aspect, horizontally and vertically. The backdrop shader maps them to the
 * screen edges, so the home view keeps the look it was tuned for.
 */
export function backdropExtent(aspect: number): [number, number] {
  const halfY = (HOME_FOV * Math.PI) / 360;
  const halfX = Math.atan(Math.tan(halfY) * aspect);
  return [sphereAngle(halfX), sphereAngle(halfY)];
}

/** Angle from the centre at which a home-camera ray `off` radians off-axis meets the sphere. */
function sphereAngle(off: number): number {
  const c = HOME_CAMERA_Z;
  const k = Math.cos(off);
  const t = c * k + Math.sqrt(c * c * k * k - c * c + BACKDROP_RADIUS * BACKDROP_RADIUS);
  return Math.atan2(t * Math.sin(off), t * k - c);
}

export interface UniverseData {
  /** Per point: rest position xyz, seed. */
  data: Float32Array;
  /** Per point: warmth, kind (0 dust, 1 haze, 2 glint). */
  style: Float32Array;
}

type Vec3 = [number, number, number];

interface Cluster {
  centre: Vec3;
  /** Long axis, so some clumps read as filaments. */
  axis: Vec3;
  spread: number;
  stretch: number;
  warm: number;
}

/**
 * Built from a fixed seed, so every boot draws the same universe. Haze points come last.
 * The clumps don't depend on the counts, so a sparser field keeps the same layout.
 */
export function universeData(dustCount: number, hazeCount: number): UniverseData {
  const random = mulberry32(0x5eed);
  const gauss = (): number => {
    const u = 1 - random();
    const v = random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };
  // Uniform on the sphere cap facing the camera.
  const direction = (maxZ: number): Vec3 => {
    const z = -1 + (maxZ + 1) * random();
    const r = Math.sqrt(1 - z * z);
    const phi = random() * Math.PI * 2;
    return [Math.cos(phi) * r, Math.sin(phi) * r, z];
  };
  const scale = (v: Vec3, s: number): Vec3 => [v[0] * s, v[1] * s, v[2] * s];
  const INNER = UNIVERSE_INNER;
  const OUTER = UNIVERSE_OUTER;

  const clusters: Cluster[] = Array.from({ length: CLUSTER_COUNT }, (_, i) => ({
    // Well inside the cap, so no clump sits on the faded rim.
    centre: scale(direction(-0.2), INNER + 3 + (OUTER * 0.7 - INNER - 3) * random()),
    axis: direction(1),
    spread: 1.2 + 1.3 * random(),
    stretch: random() < 0.5 ? 1 : 2.5 + 2 * random(),
    warm: i % 4 === 3 ? 0.15 + 0.2 * random() : 0.8 + 0.2 * random(),
  }));
  const around = (c: Cluster, spread: number): Vec3 => {
    const along = gauss() * spread * c.stretch;
    const p: Vec3 = [
      c.centre[0] + gauss() * spread + c.axis[0] * along,
      c.centre[1] + gauss() * spread + c.axis[1] * along,
      c.centre[2] + gauss() * spread + c.axis[2] * along,
    ];
    // Never inside the pocket around the sparks.
    const r = Math.hypot(...p);
    return r < INNER ? scale(p, (INNER + random()) / Math.max(r, 1e-4)) : p;
  };

  const total = dustCount + hazeCount;
  const data = new Float32Array(total * 4);
  const style = new Float32Array(total * 2);
  for (let i = 0; i < total; i += 1) {
    const haze = i >= dustCount;
    let position: Vec3;
    let warm: number;
    let kind = haze ? 1 : 0;
    if (haze || random() < CLUMP_SHARE) {
      const c = clusters[Math.floor(random() * CLUSTER_COUNT)] as Cluster;
      position = around(c, haze ? c.spread * 0.8 : c.spread);
      warm = Math.min(1, c.warm + 0.25 * gauss());
    } else {
      // Biased outwards, so the far field fills the screen edges with small, sharp dots.
      position = scale(direction(HEMISPHERE_Z), INNER + (OUTER - INNER) * random() ** 0.6);
      // Mostly champagne, with some cool stars.
      warm = random() < WARM_STAR_SHARE ? 0.5 + 0.4 * random() : 0.2 * random() ** 3;
      if (random() < GLINT_SHARE) kind = 2;
    }
    data.set([...position, random()], i * 4);
    style.set([Math.max(0, warm), kind], i * 2);
  }
  return { data, style };
}

function mulberry32(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
